import type { ChatEntryWithSubTables } from "../../../shared/aiSdkMessages";
import { parseChatError } from "../../../shared/chatErrors";
import type { StreamMessage } from "../../hooks/useStreamingText";
import { ErrorMessage } from "./ErrorMessage";
import { ResearchSources } from "./ResearchCitations";
import { researchCitationIds } from "./researchCitationLinks";
import { useTranscriptSettings } from "./TranscriptContext";
import { TypewriterText } from "./TypewriterText";

export function AssistantMessage({
  entry,
  stream,
  nested = false,
}: {
  entry: ChatEntryWithSubTables;
  stream?: StreamMessage;
  nested?: boolean;
}) {
  const {
    isCurrentUserChat,
    canWrite,
    retryableErrorEntryId,
    onRetryFromError,
    onTypewriterAnimatingChange,
    resolveFileHref,
  } = useTranscriptSettings();
  if (!entry.assistantMessage) return null;
  const text = stream?.text ?? entry.assistantMessage.text;
  const isStreaming = stream?.status === "streaming";
  const errorDetail = isStreaming ? null : parseChatError(text);
  if (errorDetail != null) {
    return (
      <ErrorMessage
        title={errorDetail.title}
        message={errorDetail.message}
        createdAt={entry.createdAt}
        canRetry={
          isCurrentUserChat && canWrite && entry.id === retryableErrorEntryId
        }
        onRetry={() => onRetryFromError(entry.id)}
        suggestModelSwitch={
          isCurrentUserChat && canWrite && errorDetail.isModelError
        }
      />
    );
  }
  return (
    <>
      <TypewriterText
        text={text}
        citationEntryId={entry.id}
        latencyMessageId={entry.id}
        streaming={isStreaming}
        variant={nested ? "text-small" : "text-regular"}
        onAnimatingChange={onTypewriterAnimatingChange}
        resolveFileHref={resolveFileHref}
      />
      {!isStreaming && (
        <ResearchSources
          messageId={entry.id}
          sourceIds={researchCitationIds(text)}
        />
      )}
    </>
  );
}
